// Acceso a Supabase desde la PWA. Toda respuesta con error se convierte en ApiError
// (marcando si fue un problema de red, para la cola offline).

import { FunctionsHttpError } from '@supabase/supabase-js';
import type { CardChanges } from '@shared/notion/properties.ts';
import type { CardRow } from '@shared/domain/card.ts';
import type { Rounding } from '@shared/domain/rounding.ts';
import type { ReportKind } from '@shared/domain/reports.ts';
import { supabase } from './supabase.ts';
import { ApiError, isNetworkError, looksLikeNetworkMessage } from './errors.ts';
import { fetchAllRows } from './paginate.ts';
import {
  CARD_LIST_COLUMNS,
  ENTRY_COLUMNS,
  WORK_LOG_COLUMNS,
  type CardDetail,
  type CardListItem,
  type LinkCandidate,
  type NotionUser,
  type PersonSummary,
  type Profile,
  type SyncStatus,
  type SyncTrigger,
  type TimeEntry,
  type WorkLog,
} from './types.ts';

function check<T>(result: { data: T; error: { message: string } | null }): T {
  if (result.error) {
    throw new ApiError(result.error.message, looksLikeNetworkMessage(result.error.message));
  }
  return result.data;
}

/** Para consultas de listas: sin error, `data` nunca es null. */
function rows<T>(result: { data: T[] | null; error: { message: string } | null }): T[] {
  return check(result) ?? [];
}

async function invoke<T>(name: string, body: Record<string, unknown>): Promise<T> {
  const { data, error } = await supabase.functions.invoke<T>(name, { body });
  if (error) {
    if (error instanceof FunctionsHttpError) {
      let message = `Error ${error.context.status}`;
      try {
        const payload = (await error.context.json()) as { error?: string };
        if (payload.error) message = payload.error;
      } catch {
        // cuerpo no JSON
      }
      throw new ApiError(message, false);
    }
    throw new ApiError(error.message, isNetworkError(error));
  }
  return data as T;
}

export const api = {
  async profile(userId: string): Promise<Profile | null> {
    return check(
      await supabase
        .from('profiles')
        .select('id, email, display_name, role, notion_user_id, solo_lo_mio, redondeo')
        .eq('id', userId)
        .maybeSingle(),
    ) as Profile | null;
  },

  async linkAuto(): Promise<Profile> {
    return check(await supabase.rpc('link_notion_user_auto')) as Profile;
  },

  async linkManual(notionUserId: string): Promise<Profile> {
    return check(
      await supabase.rpc('link_notion_user', { p_notion_user_id: notionUserId }),
    ) as Profile;
  },

  async updateProfile(userId: string, patch: Partial<Pick<Profile, 'solo_lo_mio' | 'redondeo'>>) {
    check(await supabase.from('profiles').update(patch).eq('id', userId));
  },

  async notionUsers(): Promise<NotionUser[]> {
    return rows(
      await supabase.from('notion_users').select('id, name, email, avatar_url').order('name'),
    );
  },

  async peopleForLink(): Promise<LinkCandidate[]> {
    return rows(await supabase.rpc('notion_people_for_link')) as LinkCandidate[];
  },

  async people(): Promise<PersonSummary[]> {
    return rows(await supabase.from('profiles').select('id, email, display_name, notion_user_id'));
  },

  /** Todas las tarjetas en caché, por rangos (PostgREST corta en 1000 filas). */
  async cards(): Promise<CardListItem[]> {
    const rows = await fetchAllRows<CardListItem>((from, to) =>
      supabase
        .from('cards')
        .select(CARD_LIST_COLUMNS, { count: 'exact' })
        .order('id')
        .range(from, to)
        .overrideTypes<CardListItem[], { merge: false }>(),
    );
    // Si una sincronización cambió la tabla entre páginas, se descartan duplicados.
    return [...new Map(rows.map((c) => [c.id, c])).values()];
  },

  async card(id: string): Promise<CardDetail | null> {
    return check(
      await supabase
        .from('cards')
        .select(`${CARD_LIST_COLUMNS}, body, body_expires_at, body_synced_at`)
        .eq('id', id)
        .maybeSingle(),
    ) as CardDetail | null;
  },

  /** Registros por día de una tarjeta (de todas las personas), del más reciente al más antiguo. */
  async logsForCard(cardId: string): Promise<WorkLog[]> {
    return rows(
      await supabase
        .from('work_logs')
        .select(WORK_LOG_COLUMNS)
        .eq('card_id', cardId)
        .is('deleted_at', null)
        .order('work_date', { ascending: false }),
    ) as WorkLog[];
  },

  /** Registros propios desde una fecha (los que usan la hoja de horas y los avisos). */
  async myLogs(userId: string, since: string): Promise<WorkLog[]> {
    return rows(
      await supabase
        .from('work_logs')
        .select(WORK_LOG_COLUMNS)
        .eq('user_id', userId)
        .gte('work_date', since)
        .is('deleted_at', null),
    ) as WorkLog[];
  },

  async setWorkLog(log: {
    card_id: string;
    work_date: string;
    seconds: number;
    user_id?: string;
  }): Promise<WorkLog> {
    return check(
      await supabase.rpc('set_work_log', {
        p_card_id: log.card_id,
        p_work_date: log.work_date,
        p_seconds: log.seconds,
        p_user_id: log.user_id ?? null,
      }),
    ) as WorkLog;
  },

  async deleteWorkLog(log: { card_id: string; work_date: string; user_id?: string }) {
    check(
      await supabase.rpc('delete_work_log', {
        p_card_id: log.card_id,
        p_work_date: log.work_date,
        p_user_id: log.user_id ?? null,
      }),
    );
  },

  async openEntry(userId: string): Promise<TimeEntry | null> {
    return check(
      await supabase
        .from('time_entries')
        .select(ENTRY_COLUMNS)
        .eq('user_id', userId)
        .is('ended_at', null)
        .is('deleted_at', null)
        .maybeSingle(),
    ) as TimeEntry | null;
  },

  async startTimer(cardId: string, at?: string): Promise<TimeEntry> {
    return check(
      await supabase.rpc('start_timer', { p_card_id: cardId, p_started_at: at ?? null }),
    ) as TimeEntry;
  },

  async stopTimer(at?: string): Promise<TimeEntry | null> {
    return check(await supabase.rpc('stop_timer', { p_ended_at: at ?? null })) as TimeEntry | null;
  },

  async updateCard(cardId: string, changes: CardChanges): Promise<CardRow> {
    const data = await invoke<{ card: CardRow }>('update-card', { card_id: cardId, changes });
    return data.card;
  },

  async sync(trigger: SyncTrigger): Promise<void> {
    await invoke('sync-notion', { mode: 'incremental', trigger });
  },

  async syncPage(pageId: string): Promise<void> {
    await invoke('sync-notion', { mode: 'page', page_id: pageId, trigger: 'manual' });
  },

  async syncStatus(): Promise<SyncStatus | null> {
    return check(await supabase.rpc('get_sync_status')) as SyncStatus | null;
  },

  async searchClosed(query: string): Promise<CardRow[]> {
    const data = await invoke<{ cards: CardRow[] }>('search-closed', { query });
    return data.cards;
  },

  async report(
    kind: ReportKind,
    from: string,
    to: string,
    rounding: Rounding,
  ): Promise<Record<string, unknown>[]> {
    const fn = {
      persona_semana: 'report_person_week',
      tarjeta: 'report_card',
      etiqueta: 'report_etiqueta',
      marca: 'report_marca',
    }[kind];
    return check(
      await supabase.rpc(fn, { p_from: from, p_to: to, p_rounding: rounding }),
    ) as Record<string, unknown>[];
  },
};
